import { EditIcon, SupportIcon } from '@approvalmax/ui';
import { Button, Flex, Text } from '@approvalmax/ui/src/components';
import { memo, useMemo } from 'react';
import { useIsMyAccountAvailable } from 'shared/hooks';

import { PropertyRowWithButton } from '../../../PropertyRowWithButton/PropertyRowWithButton';
import { EmailChangeViaSupportPopup } from '../EmailChangeViaSupportPopup/EmailChangeViaSupportPopup';
import { EmailChangeViaSupportRequestedPopup } from '../EmailChangeViaSupportRequestedPopup/EmailChangeViaSupportRequestedPopup';
import { useContactSupportForm, useEmailChangeForm } from './SubsectionEmail.hooks';
import { Loading } from './SubsectionEmail.loading';
import { messages } from './SubsectionEmail.messages';
import { SubsectionEmailProps } from './SubsectionEmail.types';

export const SubsectionEmail = memo<SubsectionEmailProps>((props) => {
    const { profile, activeTicket, isLoading } = props;

    const isMyAccountAvailable = useIsMyAccountAvailable();

    const {
        progress,
        isContactSupportFormVisible,
        isRequestedPopupVisible,
        handleCancelRequest,
        handleShowContactSupportForm,
        handleHideContactSupportForm,
        handleShowEmailChangeRequestedPopup,
        handleHideEmailChangeRequestedPopup,
    } = useContactSupportForm({
        ticketId: activeTicket?.ticketId,
    });

    const { handleShowEmailChangeForm } = useEmailChangeForm();

    const renderAction = useMemo(() => {
        if (isMyAccountAvailable && activeTicket) {
            return (
                <Button size='small' color='red40' onClick={handleCancelRequest} progress={progress}>
                    {messages.buttonCancel}
                </Button>
            );
        }

        if (isMyAccountAvailable && !activeTicket) {
            return (
                <Button
                    size='small'
                    color='blue10'
                    onClick={handleShowContactSupportForm}
                    startIcon={<SupportIcon size={12} />}
                    progress={progress}
                >
                    {messages.buttonContactSupport}
                </Button>
            );
        }

        return (
            <Button
                size='small'
                color='blue10'
                onClick={handleShowEmailChangeForm}
                startIcon={<EditIcon size={12} />}
                progress={progress}
            >
                {messages.buttonChange}
            </Button>
        );
    }, [
        isMyAccountAvailable,
        activeTicket,
        handleShowEmailChangeForm,
        progress,
        handleCancelRequest,
        handleShowContactSupportForm,
    ]);

    const renderDescription = useMemo(() => {
        let description: string = messages.descriptionForUser;

        if (isMyAccountAvailable && activeTicket) {
            description = messages.descriptionForAdminRequested({
                newEmail: (
                    <Text font='body' fontSize='small' wrap={false} fontWeight='medium'>
                        {activeTicket.targetEmail}
                    </Text>
                ),
            });
        }

        if (isMyAccountAvailable && !activeTicket) {
            description = messages.descriptionForAdmin;
        }

        return (
            <Flex direction='column' spacing='0'>
                <Text font='body' fontSize='small' fontWeight='bold' color='midnight100'>
                    {profile.email}
                </Text>

                <Text font='body' fontSize='small' fontWeight='regular' color='midnight70'>
                    {description}
                </Text>
            </Flex>
        );
    }, [isMyAccountAvailable, activeTicket, profile.email]);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <>
            <PropertyRowWithButton
                title={messages.title}
                description={renderDescription}
                actions={renderAction}
                statusDisabledText=''
                statusEnabledText=''
            />

            {isContactSupportFormVisible && (
                <EmailChangeViaSupportPopup
                    open
                    onClose={handleHideContactSupportForm}
                    onNext={handleShowEmailChangeRequestedPopup}
                />
            )}

            {isRequestedPopupVisible && (
                <EmailChangeViaSupportRequestedPopup open onClose={handleHideEmailChangeRequestedPopup} />
            )}
        </>
    );
});

SubsectionEmail.displayName = 'SubsectionEmail';
